/**
 * Dexie-backed queue of historical (offline) charging sessions. The user
 * fills the form, we persist via OfflineQueueRepository. When the station
 * is connected, "Flush now" hands the queue to OfflineSessionRunner which
 * walks each row through StartTransaction → N × MeterValues → StopTransaction.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import { OfflineQueueRepositoryDexie } from '@/persistence/dexie/repositories';
import {
  OfflineSession,
  runOfflineQueue,
  type OfflineSessionSnapshot,
  type OfflineUploadPort,
  type OfflineRunReport,
  DomainEventEmitter,
} from '@/domain';
import { OfflineSessionStatus, type StationId } from '@/domain/types';

export interface OfflineQueueStoreState {
  readonly entries: readonly OfflineSessionSnapshot[];
  readonly status: 'idle' | 'loading' | 'ready' | 'flushing';
  readonly lastReport: OfflineRunReport | null;
}

export interface OfflineQueueStoreActions {
  refresh(stationId: StationId): Promise<void>;
  addManual(stationId: StationId, snap: OfflineSessionSnapshot): Promise<void>;
  remove(stationId: StationId, queueId: string): Promise<void>;
  clearAll(stationId: StationId): Promise<void>;
  flushNow(stationId: StationId, port: OfflineUploadPort): Promise<OfflineRunReport>;
}

export type OfflineQueueStore = OfflineQueueStoreState & OfflineQueueStoreActions;

const repo = new OfflineQueueRepositoryDexie(db);
const emitter = new DomainEventEmitter();

export const useOfflineQueueStore = create<OfflineQueueStore>((set, get) => ({
  entries: [],
  status: 'idle',
  lastReport: null,

  async refresh(stationId: StationId): Promise<void> {
    set({ status: 'loading' });
    const entries = await repo.byStation(stationId);
    set({ entries, status: 'ready' });
  },

  async addManual(stationId: StationId, snap: OfflineSessionSnapshot): Promise<void> {
    // Validate via the domain entity so an invalid form catches before persist.
    void new OfflineSession(snap);
    await repo.upsert(stationId, snap);
    set({ entries: await repo.byStation(stationId) });
  },

  async remove(stationId: StationId, queueId: string): Promise<void> {
    await repo.delete(stationId, queueId);
    set({ entries: await repo.byStation(stationId) });
  },

  async clearAll(stationId: StationId): Promise<void> {
    for (const s of get().entries) {
      await repo.delete(stationId, s.queueId);
    }
    set({ entries: [] });
  },

  async flushNow(stationId: StationId, port: OfflineUploadPort): Promise<OfflineRunReport> {
    set({ status: 'flushing' });
    const report = await runOfflineQueue(stationId, emitter, repo, port);
    const entries = await repo.byStation(stationId);
    set({ entries, status: 'ready', lastReport: report });
    return report;
  },
}));

/** Build a defaults-filled OfflineSessionSnapshot from a form view-model. */
export function buildOfflineSnapshot(input: {
  readonly queueId: string;
  readonly agoSec: number;
  readonly duration: number;
  readonly idTag: string;
  readonly connectorId: number;
  readonly meterStartWh: number;
  readonly energyDeliveredWh: number;
  readonly meterValueIntervalSec: number;
  readonly voltage: number;
  readonly stopReason: OfflineSessionSnapshot['stopReason'];
  readonly startSoC?: number;
  readonly endSoC?: number;
  readonly nowMs: number;
}): OfflineSessionSnapshot {
  return {
    queueId: input.queueId,
    idTag: input.idTag,
    connectorId: input.connectorId as unknown as OfflineSessionSnapshot['connectorId'],
    startedAt: input.nowMs - input.agoSec * 1000,
    duration: input.duration,
    meterStartWh: input.meterStartWh,
    energyDeliveredWh: input.energyDeliveredWh,
    meterValueIntervalSec: input.meterValueIntervalSec,
    ...(input.startSoC !== undefined ? { startSoC: input.startSoC } : {}),
    ...(input.endSoC !== undefined ? { endSoC: input.endSoC } : {}),
    voltage: input.voltage,
    stopReason: input.stopReason,
    status: OfflineSessionStatus.Queued,
  };
}
